import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, RefreshCw, Trash2, CheckCircle, Clock, XCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { el } from "date-fns/locale";

interface ConsultingLead {
  id: string;
  name: string;
  email: string;
  phone: string;
  business: string;
  problem: string;
  status: string | null;
  notes: string | null;
  created_at: string;
}

const ConsultingDashboard = () => {
  const [leads, setLeads] = useState<ConsultingLead[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchLeads = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("consulting_leads")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η φόρτωση των leads",
        variant: "destructive",
      });
    } else {
      setLeads(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const updateStatus = async (id: string, status: string) => {
    const { error } = await supabase
      .from("consulting_leads")
      .update({ status })
      .eq("id", id);

    if (error) {
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η ενημέρωση",
        variant: "destructive",
      });
    } else {
      setLeads(leads.map(lead => 
        lead.id === id ? { ...lead, status } : lead
      ));
      toast({
        title: "Επιτυχία",
        description: "Η κατάσταση ενημερώθηκε",
      });
    }
  };

  const deleteLead = async (id: string) => {
    if (!confirm("Σίγουρα θέλεις να διαγράψεις αυτό το lead;")) return;

    const { error } = await supabase
      .from("consulting_leads")
      .delete()
      .eq("id", id);

    if (error) {
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η διαγραφή",
        variant: "destructive",
      });
    } else {
      setLeads(leads.filter(lead => lead.id !== id));
      toast({
        title: "Διαγράφηκε",
        description: "Το lead διαγράφηκε επιτυχώς",
      });
    }
  };

  const getStatusIcon = (status: string | null) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "cancelled":
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusLabel = (status: string | null) => {
    switch (status) {
      case "completed":
        return "Ολοκληρώθηκε";
      case "cancelled":
        return "Ακυρώθηκε";
      default:
        return "Σε αναμονή";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold">1-1 Leads</h1>
              <p className="text-muted-foreground text-sm">
                Διαχείριση αιτημάτων συμβουλευτικής
              </p>
            </div>
          </div>
          <Button onClick={fetchLeads} variant="outline" size="sm">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Ανανέωση
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-muted-foreground text-sm">Σύνολο</p>
            <p className="text-2xl font-bold">{leads.length}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-muted-foreground text-sm">Σε αναμονή</p>
            <p className="text-2xl font-bold text-yellow-500">
              {leads.filter(l => !l.status || l.status === "pending").length}
            </p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-muted-foreground text-sm">Ολοκληρωμένα</p>
            <p className="text-2xl font-bold text-green-500">
              {leads.filter(l => l.status === "completed").length}
            </p>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            Δεν υπάρχουν leads ακόμα
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ημερομηνία</TableHead>
                  <TableHead>Όνομα</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Τηλέφωνο</TableHead>
                  <TableHead>Επιχείρηση</TableHead>
                  <TableHead className="max-w-[250px]">Πρόβλημα</TableHead>
                  <TableHead>Κατάσταση</TableHead>
                  <TableHead className="text-right">Ενέργειες</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(lead.created_at), "dd MMM yyyy, HH:mm", { locale: el })}
                    </TableCell>
                    <TableCell className="font-medium">{lead.name}</TableCell>
                    <TableCell>
                      <a href={`mailto:${lead.email}`} className="text-primary hover:underline">
                        {lead.email}
                      </a>
                    </TableCell>
                    <TableCell>
                      <a href={`tel:${lead.phone}`} className="text-primary hover:underline">
                        {lead.phone}
                      </a>
                    </TableCell>
                    <TableCell>{lead.business}</TableCell>
                    <TableCell className="max-w-[250px] truncate" title={lead.problem}>
                      {lead.problem}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={lead.status || "pending"}
                        onValueChange={(value) => updateStatus(lead.id, value)}
                      >
                        <SelectTrigger className="w-[140px]">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(lead.status)}
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-yellow-500" />
                              Σε αναμονή
                            </div>
                          </SelectItem>
                          <SelectItem value="completed">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              Ολοκληρώθηκε
                            </div>
                          </SelectItem>
                          <SelectItem value="cancelled">
                            <div className="flex items-center gap-2">
                              <XCircle className="w-4 h-4 text-red-500" />
                              Ακυρώθηκε
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteLead(lead.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsultingDashboard;
